class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.modal = this.querySelector('.quick-view');
    this.dialog = this.querySelector('#QuickView');
    this.contents = this.querySelector('#QuickView-contents');
    this.products = new Map();

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));

    this.addButtons();
    // Filters, sorting and recommendations all add product cards after the page loads.
    new MutationObserver(() => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.addButtons());
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  }

  get isOpen() {
    return !this.modal.hidden;
  }

  addButtons() {
    document.querySelectorAll('.card-wrapper:not([data-quick-view])').forEach((card) => {
      card.dataset.quickView = '';
      const link = card.querySelector('a.full-unstyled-link[href*="/products/"]');
      if (!link) return;

      const url = link.getAttribute('href');
      const title = link.textContent.trim();
      const view = this.createButton('card-quick-view', `Quick view ${title}`);
      view.dataset.quickViewOpen = url;
      view.setAttribute('aria-haspopup', 'dialog');
      card.appendChild(view);

      // Quick add is off in some templates and sections, so those cards get their own + button.
      if (card.querySelector('.quick-add__submit') || /sold out/i.test(card.textContent)) return;
      const add = this.createButton('card-quick-add', `Add ${title} to cart`);
      add.dataset.cardQuickAdd = url;
      card.appendChild(add);
    });
  }

  createButton(className, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    return button;
  }

  onDocumentClick(event) {
    const opener = event.target.closest('[data-quick-view-open]');
    if (opener) return this.open(opener);

    const quickAdd = event.target.closest('[data-card-quick-add]');
    if (quickAdd) return this.quickAdd(quickAdd);

    if (!this.isOpen) return;

    if (event.target.closest('[data-quick-view-close]')) return this.close();

    const thumbnail = event.target.closest('[data-quick-view-image]');
    if (thumbnail) return this.showImage(thumbnail);

    const step = event.target.closest('[data-quick-view-step]');
    if (step) return this.changeQuantity(Number(step.dataset.quickViewStep));

    const add = event.target.closest('[data-quick-view-add]');
    if (add) {
      const quantity = Number(this.querySelector('#QuickViewQuantity').textContent);
      const opener = this.opener;
      this.close();
      this.addToCart(Number(add.dataset.quickViewAdd), quantity, opener);
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  // Products with options need a choice first, so those open the product view.
  quickAdd(button) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    const url = button.dataset.cardQuickAdd;
    fetch(`${url.split('?')[0]}.js`)
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((product) => {
        const [variant] = product.variants;
        if (product.variants.length === 1 && variant.available) {
          this.addToCart(variant.id, 1, button);
        } else {
          this.open(button, url);
        }
      })
      .catch(() => {
        window.location.href = url;
      })
      .finally(() => button.classList.remove('is-loading'));
  }

  open(opener, url = opener.dataset.quickViewOpen) {
    this.opener = opener;
    this.request = url;
    this.contents.innerHTML = '';
    this.modal.hidden = false;
    this.modal.classList.add('is-loading');
    document.body.classList.add('overflow-hidden');
    this.dialog.focus({ preventScroll: true });

    this.getProduct(url)
      .then((html) => {
        if (this.request !== url || !this.isOpen) return;
        const source = new DOMParser().parseFromString(html, 'text/html').getElementById('QuickViewContent');
        this.contents.innerHTML = source.outerHTML;
        this.dialog.setAttribute('aria-label', source.dataset.title);
        this.modal.classList.remove('is-loading');
        trapFocus(this.dialog, this.dialog);
      })
      .catch(() => {
        window.location.href = url;
      });
  }

  close() {
    this.request = null;
    this.modal.hidden = true;
    this.modal.classList.remove('is-loading');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.opener);
    if (this.opener) this.opener.focus({ preventScroll: true });
  }

  getProduct(url) {
    if (!this.products.has(url)) {
      const request = new URL(url, window.location.origin);
      request.searchParams.set('section_id', 'quick-view-content');
      this.products.set(
        url,
        fetch(request).then((response) => (response.ok ? response.text() : Promise.reject()))
      );
    }
    return this.products.get(url);
  }

  showImage(thumbnail) {
    const image = this.querySelector('#QuickViewImage');
    if (!image) return;
    image.removeAttribute('srcset');
    image.src = thumbnail.dataset.quickViewImage;
    this.querySelectorAll('[data-quick-view-image]').forEach((item) => item.removeAttribute('aria-current'));
    thumbnail.setAttribute('aria-current', 'true');
  }

  changeQuantity(step) {
    const quantity = this.querySelector('#QuickViewQuantity');
    quantity.textContent = Math.max(1, Number(quantity.textContent) + step);
  }

  addToCart(variantId, quantity, opener) {
    const cart = document.querySelector('mini-cart');
    cart.setActiveElement(opener);
    cart.addItem(variantId, quantity);
    // Waits for this click to finish, or the mini cart would treat it as a click outside and close.
    setTimeout(() => {
      if (!cart.isOpen) cart.open();
    });
  }
}

customElements.define('quick-view-modal', QuickViewModal);
